/**
 * Which path a text-on-path actually rides — direction (WMPCM-9530, Story 3) and perpendicular
 * offset (WMPCM-10320 Bug 3) both resolved here, because both are properties of the GEOMETRY.
 *
 * `<textPath>` has no way to run text backwards and no way to hold it off the path without moving
 * the glyphs individually, so each is expressed as a derived `d` instead — reversed, offset, or
 * both. One function owns that so no caller has to know the order they compose in.
 *
 * **Every outline reverses.** A flip always changes the direction of travel, so the text reads the
 * other way and — because rotation follows the raw path tangent — ends up mirrored. What differs
 * between outlines is only WHERE the reversed text starts.
 *
 *  - **Closed outline** (circle, polygon, anything that wraps): anchored at the start position.
 *    The text begins on the same point of the outline and runs the other way around it, which is
 *    what `circle-text-clockwise` / `circle-text-counterclockwise` show. The legacy demo does this
 *    by reordering points as `[pts[0], ...rest.reverse()]` — first point pinned, remainder
 *    reversed — and anchoring the start offset is the equivalent.
 *  - **Open path** (a pen line, a straight line): the text CANNOT stay anchored, because an open
 *    path has nothing before its start point to flow into. Measured in Chrome: a `startOffset` of
 *    `pathLength` — which is what anchoring maps to at a start distance of 0, the default — renders
 *    nothing at all, every glyph collapsing to the origin. So the start distance is instead
 *    measured from the reversed path's own start, and the text moves to the far end and reads back
 *    along the line. `mirrored` reports that so the start handle can follow it.
 *
 * ## Divergence from legacy, deliberate
 *
 * The demo does NOT reverse open paths — it keeps the reading order and mirrors the perpendicular
 * offset instead, flipping which side of the line the text rides ("Open paths never wrap and ignore
 * `direction` here … not by reversing point order"), and `path-text-counterclockwise` draws it that
 * way. That was implemented first and rejected in review: on a STRAIGHT line "which side" is the
 * only thing left, so the control reduced to a 12pt vertical nudge that reads as nothing happening,
 * and it degenerated to an exact no-op whenever the offset was 0 — reachable through the Offset
 * control's `Natural` preset, which doubles as its reset.
 *
 * Reversing everything makes the control mean one thing on every shape and never render
 * identically in both states. Flag it as a parity divergence if legacy behaviour is the acceptance
 * bar.
 *
 * ## Offset (WMPCM-10320 Bug 3)
 *
 * Baked into the geometry, mitered at corners, rather than applied per glyph — see
 * `withBakedOffset` and `offset-svg-path.ts`. It is baked AFTER any reversal, matching legacy
 * (`buildPathSegs` orders its points by direction, then offsets), and matching what the per-glyph
 * `dy` it replaces already did: reversing flips the tangent, so it flips which side the text sits
 * on. Existing artwork therefore keeps its side.
 *
 * Glyph orientation is deliberately left alone. Rotation follows the raw path tangent with no
 * upright correction, so the flip both reverses the reading direction AND leaves the text
 * upside-down on the reverse side of a loop or a sharp corner. Those happen together, and the demo
 * flags that as intentional legacy parity, confirmed live — do not "fix" it, because turning
 * individual glyphs upright also reverses their reading order.
 */

import { offsetSvgPath } from './helpers/offset-svg-path'
import { isClosedSvgPath, reverseSvgPath, reversedStartOffset } from './helpers/reverse-svg-path'

export interface TextOnPathDirectionRender {
  /**
   * The `d` for the `<textPath>` to follow, or `null` to follow the shape's own path.
   *
   * Non-null when the direction is reversed, when the perpendicular offset has been baked into the
   * geometry (WMPCM-10320 Bug 3), or both.
   */
  textD: string | null
  /** `startOffset` for the `<textPath>`, already mapped onto whichever path `textD` selects. */
  startOffset: number
  /**
   * Perpendicular offset still left for the renderer to apply per glyph.
   *
   * **0 whenever the offset was baked into `textD`**, which is the normal case for any non-zero
   * offset. Applying it twice would double the displacement; applying it at all on a corner is the
   * bug `offset-svg-path.ts` exists to fix.
   */
  offset: number
  /**
   * True when `startDistance` is measured from the opposite end of the outline, so the text's
   * first glyph sits at `pathLength - startDistance` in FORWARD terms. Open reversed paths only.
   *
   * The start handle and its drag both work in forward distances, so they have to apply this or
   * the handle would mark a point the text no longer starts at and dragging it would run backwards.
   */
  mirrored: boolean
}

const FORWARD = (startDistance: number, offset: number): TextOnPathDirectionRender => ({
  textD: null,
  startOffset: startDistance,
  offset,
  mirrored: false,
})

/**
 * Move the perpendicular offset off the glyphs and into the path itself (WMPCM-10320 Bug 3).
 *
 * `startOffset` is a distance measured on the ORIGINAL outline, and an offset path is a different
 * length — 18% longer for a five-point star at offset 6 — so it is rescaled by the length ratio to
 * keep the first glyph on the same fraction of the outline. Without that the text would slide away
 * from the start handle as the start distance grew, by up to the whole difference.
 *
 * Rescaling rather than reporting the offset path's own length keeps `pathLength` — and therefore
 * the Position control's range and the handle — pinned to the outline, so neither jumps when the
 * user changes the offset.
 *
 * Returns the render unchanged when there is nothing to bake or the path cannot be offset; the
 * per-glyph `dy` is then exactly what shipped before, which is correct on a smooth outline and only
 * ever wrong at a corner.
 */
function withBakedOffset(render: TextOnPathDirectionRender, d: string): TextOnPathDirectionRender {
  const baked = offsetSvgPath(render.textD ?? d, render.offset)

  if (baked == null) return render

  return {
    ...render,
    textD: baked.d,
    startOffset: render.startOffset * (baked.length / baked.rawLength),
    offset: 0,
  }
}

export function resolveTextOnPathDirection({
  d,
  clockwise,
  startDistance,
  pathLength,
  offset,
}: {
  d: string
  clockwise: boolean
  startDistance: number
  pathLength: number
  offset: number
}): TextOnPathDirectionRender {
  if (clockwise) return withBakedOffset(FORWARD(startDistance, offset), d)

  const textD = reverseSvgPath(d)

  // A path this module cannot reverse falls back to the forward direction rather than rendering
  // nothing. Unreachable for the shapes in this codebase — the reverser covers the whole path
  // grammar, and a `d` broken enough to land here would already have broken the outline.
  if (textD == null) return withBakedOffset(FORWARD(startDistance, offset), d)

  if (isClosedSvgPath(d)) {
    // Anchored: the reversed path starts on the same point, so mirroring the distance puts the
    // first glyph back on the outline position the handle marks.
    return withBakedOffset(
      {
        textD,
        startOffset: reversedStartOffset(startDistance, pathLength, true),
        offset,
        mirrored: false,
      },
      d
    )
  }

  // Open: measured from the reversed path's own start. Anchoring here maps a start distance of 0
  // to `startOffset === pathLength`, which renders nothing (measured in Chrome).
  return withBakedOffset({ textD, startOffset: startDistance, offset, mirrored: true }, d)
}

/**
 * Convert between a forward-path distance and `startDistance` for the given render.
 *
 * Involutive — the same call maps either way — so the start handle's position and the drag that
 * sets it can share one function instead of hand-rolling `pathLength - x` at each site.
 */
export function forwardDistanceFor(
  render: Pick<TextOnPathDirectionRender, 'mirrored'>,
  distance: number,
  pathLength: number
): number {
  if (!render.mirrored) return distance
  if (!Number.isFinite(distance) || !Number.isFinite(pathLength) || pathLength <= 0) return 0

  return Math.min(Math.max(pathLength - distance, 0), pathLength)
}
